import { useState } from "react";
import SearchableSelect from "./SearchableSelect";
import { FILTER_OPTIONS, AGE_OPTIONS } from "@/lib/profiles";
import { useLanguage } from "@/lib/i18n";

const Field = ({ label, children }) => {
  const { t } = useLanguage();
  return (
    <div className="fm-field">
      <label className="fm-field-label">{t(label)}</label>
      {children}
    </div>
  );
};

const selectClass = "fm-input fm-select";

const SearchPanel = ({ draft, onChange, onSearch, onClear }) => {
  const { t } = useLanguage();
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <section className="fm-panel" data-testid="fm-search-panel" aria-label="Search filters">
      <div className="fm-panel-grid">
        <Field label="Looking For">
          <div className="fm-segmented" role="group" aria-label="Looking for">
            {[
              { value: "bride", label: "Bride" },
              { value: "groom", label: "Groom" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`fm-segment${draft.lookingFor === opt.value ? " is-active" : ""}`}
                aria-pressed={draft.lookingFor === opt.value}
                data-testid={`fm-looking-${opt.value}`}
                onClick={() => onChange("lookingFor", opt.value)}
              >
                {t(opt.label)}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Age From">
          <select
            className={selectClass}
            value={draft.ageFrom}
            data-testid="fm-age-from"
            onChange={(e) => onChange("ageFrom", e.target.value)}
          >
            <option value="">{t("Any")}</option>
            {AGE_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </Field>

        <Field label="Age To">
          <select
            className={selectClass}
            value={draft.ageTo}
            data-testid="fm-age-to"
            onChange={(e) => onChange("ageTo", e.target.value)}
          >
            <option value="">{t("Any")}</option>
            {AGE_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </Field>

        <Field label="Country">
          <SearchableSelect
            options={FILTER_OPTIONS.countries}
            value={draft.country}
            onChange={(v) => onChange("country", v)}
            placeholder="All Countries"
            testId="fm-country"
          />
        </Field>

        <Field label="State / Province (Optional)">
          <input
            type="text"
            placeholder={t("Any state / province")}
            className="fm-input"
            value={draft.state}
            data-testid="fm-state"
            onChange={(e) => onChange("state", e.target.value)}
          />
        </Field>

        <Field label="City (Optional)">
          <input
            type="text"
            placeholder={t("Any city")}
            className="fm-input"
            value={draft.city}
            data-testid="fm-city"
            onChange={(e) => onChange("city", e.target.value)}
          />
        </Field>

        <Field label="Education (Optional)">
          <select
            className={selectClass}
            value={draft.education}
            data-testid="fm-education"
            onChange={(e) => onChange("education", e.target.value)}
          >
            <option value="">{t("Any Education")}</option>
            {FILTER_OPTIONS.education.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Field>

        <Field label="Profession (Optional)">
          <select
            className={selectClass}
            value={draft.profession}
            data-testid="fm-profession"
            onChange={(e) => onChange("profession", e.target.value)}
          >
            <option value="">{t("Any Profession")}</option>
            {FILTER_OPTIONS.professions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Field>

        <Field label="Marital Status">
          <select
            className={selectClass}
            value={draft.maritalStatus}
            data-testid="fm-marital-status"
            onChange={(e) => onChange("maritalStatus", e.target.value)}
          >
            <option value="">{t("Any Marital Status")}</option>
            {FILTER_OPTIONS.maritalStatus.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Field>

        <Field label="Religious / Personal Preferences (Optional)">
          <select
            className={selectClass}
            value={draft.practice}
            data-testid="fm-practice"
            onChange={(e) => onChange("practice", e.target.value)}
          >
            <option value="">{t("Any")}</option>
            {FILTER_OPTIONS.practices.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Field>

        {moreOpen && (
          <div className="fm-more-panel" data-testid="fm-more-filters-panel">
            <Field label="Height From (cm)">
              <input
                type="number"
                min="120"
                max="210"
                placeholder="e.g. 150"
                className="fm-input"
                value={draft.heightFrom}
                data-testid="fm-height-from"
                onChange={(e) => onChange("heightFrom", e.target.value)}
              />
            </Field>
            <Field label="Height To (cm)">
              <input
                type="number"
                min="120"
                max="210"
                placeholder="e.g. 185"
                className="fm-input"
                value={draft.heightTo}
                data-testid="fm-height-to"
                onChange={(e) => onChange("heightTo", e.target.value)}
              />
            </Field>
            <p className="fm-more-note">
              {t("Family background and further preferences are coming soon, insha’Allah.")}
            </p>
          </div>
        )}
      </div>

      <div className="fm-panel-actions">
        <button
          type="button"
          className="btn btn-solid btn-lg"
          data-testid="fm-search-submit"
          onClick={onSearch}
        >
          {t("Search Matches")}
        </button>
        <button
          type="button"
          className="btn btn-outline btn-lg"
          data-testid="fm-clear-filters"
          onClick={onClear}
        >
          {t("Clear Filters")}
        </button>
        <button
          type="button"
          className="fm-more-toggle"
          data-testid="fm-more-filters-toggle"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((v) => !v)}
        >
          {t("More Filters")}
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            style={{ transform: moreOpen ? "rotate(180deg)" : "none", transition: "transform .2s ease" }}
          >
            <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default SearchPanel;
